import gql from 'graphql-tag';
import {graphql} from 'react-apollo';
import {notFound, start, success, error} from '../../loading-state';
import {withAuth} from '../../../components/auth';
import {compose} from 'redux';
import {injectIntl} from 'react-intl';
import {ROLES} from '../../../utils/constants';

/**
 * @todo deprecate once partial returns tripBoard.user.profile
 * https://jira.homeawaycorp.com/browse/SCRATCHPAD-2495
 */
const getUser = (users = []) => {
    if (!Array.isArray(users)) {
        return {};
    }
    return users.find((user) => !!user.isMe);
};

export const query = gql`
query tripBoard($hav: String!, $tripBoardUuid: String!, $currency: String!) {
    tripBoard(hav: $hav, tripBoardUuid: $tripBoardUuid) {
        uuid
        name
        sharedFavorite
        stay {
          adultCount
          childrenCount
          petsIncluded
          dateRange {
            arrival
            departure
          }
        }
        user {
            role {
                type
            }
        }
        listings {
            status
            multiUnitProperty
            availableForDates
            averagePrice {
                achievableEndDate
                achievableStartDate
                currencySymbol
                currencyUnits
                localized
                periodType
                value
            }
            geoCode {
                latitude
                longitude
            }
            priceSummary(currency: $currency) {
                formattedAmount
                pricePeriodDescription
                edapEventJson
            }
            preferredPlace {
                name {
                    full
                    simple
                }
                uuid
            }
            baseLocation {
                name {
                    full
                }
            }
            bathrooms {
                full
                half
                toiletOnly
            }
            instantBookable
            bedrooms
            sleeps
            headline
            listingId
            unitApiUrl
            detailPageUrl
            reviewCount
            averageRating
            images {
                c6_uri,
                c10_uri,
                height,
                width
            }
        }
        users {
            isMe
            role {
                type
            }
            profile {
                firstName
                lastName
                publicImageUrl
            }
        }
    }
}`;

const options = (props) => { // eslint-disable-line
    return {
        variables: {
            tripBoardUuid: props.match.params.boardId,
            hav: props.hav,
            currency: props.currency
        },
        notifyOnNetworkStatusChange: true,
        errorPolicy: 'all'
    };
};

// ported from ListingScratchpadConverter.getBedroomsPresentationModel (api-scratchpad-v2)
export const createGetBedrooms = (formatMessage) => {
    const studioMessageDescriptor = {
        id: 'header.scratchpadSummary.listingsSummary.hit.label.studio',
        defaultMessage: 'Studio',
        description: 'studio label'
    };
    const bedroomsMessageDescriptor = {
        id: 'header.scratchpadSummary.listingsSummary.hit.label.bedrooms',
        defaultMessage: '',
        description: 'bedrooms label'
    };

    return function(listing) {
        if (listing.bedrooms === 0) {
            return formatMessage(studioMessageDescriptor);
        }

        if (listing.bedrooms > 1) {
            return formatMessage(bedroomsMessageDescriptor, {0: listing.bedrooms});
        }
        return '';
    };
};

export const createGetBathrooms = (formatMessage) => {
    const fullBathroomsDescriptor = {
        id: 'header.scratchpadSummary.listingsSummary.hit.label.fullBathrooms',
        defaultMessage: 'Bathrooms',
        description: 'Bathrooms label'
    };

    const halfBathsroomsDescriptor = {
        id: 'header.scratchpadSummary.listingsSummary.hit.label.halfBathrooms',
        defaultMessage: 'Half baths',
        description: 'half baths label'
    };

    return function(listing) {
        if (!listing.bathrooms) {
            return '';
        }
        const bathroomMessages = [];
        if (listing.bathrooms.full >= 1) {
            bathroomMessages.push(formatMessage(fullBathroomsDescriptor, {0: listing.bathrooms.full}));
        }
        if (listing.bathrooms.half >= 1) {
            bathroomMessages.push(formatMessage(halfBathsroomsDescriptor, {0: listing.bathrooms.half}));
        }
        // Gross but scratchpad hard-coded this too
        return bathroomMessages.join(', ');
    };
};

// ported from ListingScratchpadConverter.getSleepsPresentationModel (api-scratchpad-v2)
export const createGetSleeps = (formatMessage) => {
    const sleepsDescriptor = {
        id: 'header.scratchpadSummary.listingsSummary.hit.label.sleepsPlural',
        defaultMessage: 'sleeps',
        description: 'sleeps label'
    };

    return function(listing) {
        if (listing.sleeps >= 1) {
            return formatMessage(sleepsDescriptor, {0: listing.sleeps});
        }
        return '';
    };
};

// gross helper function that maps listings from graphql to ugly scratchpad-style hit props
function mapListingsToLegacyHitProps(intl, tripBoard) {
    const {listings, user} = tripBoard;
    const {formatMessage} = intl;
    // create format functions from translation object (for reuse within loop)
    const getBathrooms = createGetBathrooms(formatMessage);
    const getBedrooms = createGetBedrooms(formatMessage);
    const getSleeps = createGetSleeps(formatMessage);

    let listingIds = [];
    let places = {};
    const mappedListings = listings.filter((listing) => listing && (user && user.role && user.role.type !== 'VIEWER' || (listing.status && listing.status !== 'DELISTED'))).map((listing) => {
        const unitUuid = listing.unitApiUrl && listing.unitApiUrl.split('/').pop() || '';
        listingIds.push(listing.listingId);
        const images = listing.images && listing.images.map(({c10_uri: src, c6_uri: lowResSrc, ...rest}) => ({src, lowResSrc, ...rest})) || [];
        if (listing.preferredPlace && listing.preferredPlace.name) {
            const thisPlaceKey = listing.preferredPlace.name.full;
            if (!places[thisPlaceKey]) {
                places[thisPlaceKey] = {
                    ...listing.preferredPlace.name,
                    count: 0,
                    uuid: listing.preferredPlace.uuid
                };
            }
            places[thisPlaceKey].count++;
        }

        const hitProps = {
            availableForDates: listing.availableForDates,
            averageRating: listing.averageRating,
            baseLocation: listing.baseLocation,
            boardId: tripBoard.uuid,
            headline: listing.headline,
            listingId: listing.listingId,
            detailPageUrl: listing.detailPageUrl,
            geoCode: listing.geoCode,
            unitUuid,
            reviewCount: listing.reviewCount,
            averagePrice: {
                ...listing.averagePrice,
                currencyUnits: listing.averagePrice && listing.averagePrice.currencyUnits || 'USD'
            },
            bedrooms: getBedrooms(listing),
            bathrooms: getBathrooms(listing),
            sleeps: getSleeps(listing),
            images,
            instantBookable: listing.instantBookable,
            instantBookableMessage: formatMessage({id: 'shared.stabHit.instant.flag'}),
            instantBookableTooltip: formatMessage({id: 'shared.stabHit.instant.tooltip'}),
            multiUnitProperty: listing.multiUnitProperty,
            multiUnitPropertyMessage: formatMessage({id: 'shared.stabHit.multi.unit'}),
            noPrice: formatMessage({id: 'shared.stabHit.rate.none'}),
            priceSummary: listing.priceSummary,
            dateNotAvailable: formatMessage({id: 'shared.stabHit.dates.not.available'}),
            galleryUrl: images[0] && images[0].src,
            thumbnailUrl1: images[0] && images[0].src,
            thumbnailUrl2: images[1] && images[1].src,
            thumbnailUrl3: images[2] && images[2].src,
            role: user && user.role && user.role.type || ROLES.VIEWER,
            stay: tripBoard.stay,
            tripBoardUuid: tripBoard.uuid
        };
        if (tripBoard.stay && tripBoard.stay.dateRange && listing.status !== 'DELISTED') {
            // board has dates so set legacy status flag based on availability
            hitProps.status = (listing.availableForDates)
                ? 'AVAILABLE'
                : 'UNAVAILABLE';
            if (listing.priceSummary) {
                hitProps.rate = listing.priceSummary.formattedAmount;
                hitProps.ratesUnitOfMeasure = listing.priceSummary.pricePeriodDescription;
                hitProps.edapEventJson = listing.priceSummary.edapEventJson;
            }
        } else {
            // board is dateless or delisted
            hitProps.status = listing.status;
            hitProps.rate = listing.averagePrice && listing.averagePrice.localized || '';
            hitProps.ratesUnitOfMeasure = formatMessage({id: 'shared.stabHit.rate.average.night.short'});
        }
        return hitProps;
    });
    let sortedPlaces = Object.values(places || {});
    sortedPlaces.sort((a, b) => {
        return b.count - a.count;
    });

    return {mappedListings, listingIds, sortedPlaces};
}

// maps the graphql query response to the props that TripboardDetails expects
// we'll likely want to refactor TripboardDetails to use better names
export function mapQueryResponseToLegacyProps({data, ownProps}) {
    const {intl} = ownProps;
    if (data.loading) {
        return {
            loadingState: start
        };
    }
    // TODO: more robust error-handling
    // assuming that a graphql error with tripBoard === null is a 404
    if (!data.tripBoard) {
        return {
            loadingState: notFound
        };
    }
    const tripBoard = data.tripBoard;
    // if pds calls fail it is possible to have null listings, for now we'll use existing error-state
    // this should be consistent with existing behavior without graphql
    if (!tripBoard.listings) {
        return {
            loadingState: error
        };
    }
    const listingsMaps = (intl) ? mapListingsToLegacyHitProps(intl, tripBoard) : {};
    const listings = listingsMaps.mappedListings || [];
    const listingIds = listingsMaps.listingIds || [];
    const places = listingsMaps.sortedPlaces || [];
    const props = {
        boardId: tripBoard.uuid,
        name: tripBoard.name,
        isFavorite: !!tripBoard.sharedFavorite,
        isShared: !!tripBoard.sharedFavorite,
        loadingState: success,
        role: tripBoard.user.role.type,
        listings,
        listingIds,
        places,
        refetchTripboard: data.refetch,
        users: tripBoard.users,
        /**
         * @todo deprecate once partial returns tripBoard.user.profile
         * https://jira.homeawaycorp.com/browse/SCRATCHPAD-2495
         */
        user: getUser(tripBoard.users)
    };
    // stay can be null for dateless boards and legacy endpoint omits dates if the user is a VIEWER
    if (tripBoard.stay && tripBoard.stay.dateRange && tripBoard.user.role.type !== 'VIEWER') {
        props.startDate = tripBoard.stay.dateRange.arrival;
        props.endDate = tripBoard.stay.dateRange.departure;
    }

    if (tripBoard.stay && tripBoard.user.role.type !== 'VIEWER') {
        props.adultsCount = tripBoard.stay.adultCount;
        props.childrenCount = tripBoard.stay.childrenCount;
        props.petsIncluded = tripBoard.stay.petsIncluded
    }

    return props;
}

const withTripboardQuery = compose(
    withAuth, // hav is extracted from auth context for use in query
    injectIntl,
    graphql(query, {options, props: mapQueryResponseToLegacyProps})
);

export default withTripboardQuery;



// WEBPACK FOOTER //
// ./src/universal/tripboard/details/components/with-tripboard-query.js